// components/Admin/LuckyBoxManager/LuckyBoxManager.jsx
import { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Switch,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import api from '../../../api/axios';

export default function LuckyBoxManager() {
    const [tiers, setTiers] = useState([]);
    const [loading, setLoading] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    /* ─────────── 초기 로드 ─────────── */
    useEffect(() => {
        fetchTiers();
    }, []);

    /* ─────────── API 호출 ─────────── */
    const fetchTiers = async () => {
        try {
            const res = await api.get('/api/rewards/tier-probability/');
            const data = res.data.tiers.map((t) => ({
                ...t,
                price: Number.isFinite(t.price) ? t.price : 0,
                duplicate_refund: Number.isFinite(t.duplicate_refund)
                    ? t.duplicate_refund
                    : 0,
                enabled: typeof t.enabled === 'boolean' ? t.enabled : true,
            }));
            setTiers(data);
        } catch {
            alert('등급 정보를 불러오는 데 실패했습니다.');
        }
    };

    /* ─────────── 필드 수정 핸들러 ─────────── */
    const handlePriceChange = (index, value) => {
        const list = [...tiers];
        list[index].price = parseInt(value, 10) || 0;
        setTiers(list);
    };

    const handleRefundChange = (index, value) => {
        const list = [...tiers];
        list[index].duplicate_refund = parseInt(value, 10) || 0;
        setTiers(list);
    };

    const handleToggle = (index, checked) => {
        const list = [...tiers];
        list[index].enabled = checked;
        setTiers(list);
    };

    /* ─────────── 저장 ─────────── */
    const handleSave = () => {
        setLoading(true);
        api
            .put('/api/rewards/tier-probability/', {
                tiers: tiers.map((t) => ({
                    id: t.id,
                    tier: t.tier,
                    probability: t.probability,
                    color: t.color,
                    price: t.price,
                    duplicate_refund: t.duplicate_refund, // ★ 포함
                    enabled: t.enabled,
                })),
            })
            .then(() => alert('저장 완료!'))
            .catch(() => alert('저장 실패'))
            .finally(() => setLoading(false));
    };

    /* ─────────── UI ─────────── */
    return (
        <Box sx={{ p: isMobile ? 2 : 4 }}>
            <Typography variant="h5" gutterBottom>
                🧧 럭키박스 가격 & 환급액 설정
            </Typography>

            <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>등급</TableCell>
                                <TableCell>확률&nbsp;(%)</TableCell>
                                <TableCell>색상</TableCell>
                                <TableCell>가격&nbsp;(Leaf)</TableCell>
                                <TableCell>중복 환급&nbsp;(Leaf)</TableCell>
                                <TableCell>활성화</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {tiers.map((tier, idx) => (
                                <TableRow key={tier.id || idx}>
                                    {/* 등급 */}
                                    <TableCell>
                                        <TextField value={tier.tier} size="small" disabled />
                                    </TableCell>

                                    {/* 확률 */}
                                    <TableCell>
                                        <TextField value={tier.probability} size="small" disabled />
                                    </TableCell>

                                    {/* 색상 */}
                                    <TableCell>
                                        <TextField value={tier.color} size="small" disabled />
                                    </TableCell>

                                    {/* 가격 */}
                                    <TableCell>
                                        <TextField
                                            type="number"
                                            size="small"
                                            value={tier.price}
                                            onChange={(e) =>
                                                handlePriceChange(idx, e.target.value)
                                            }
                                            inputProps={{ min: 0 }}
                                        />
                                    </TableCell>

                                    {/* 중복 환급 */}
                                    <TableCell>
                                        <TextField
                                            type="number"
                                            size="small"
                                            value={tier.duplicate_refund}
                                            onChange={(e) =>
                                                handleRefundChange(idx, e.target.value)
                                            }
                                            inputProps={{ min: 0 }}
                                        />
                                    </TableCell>

                                    {/* 활성화 */}
                                    <TableCell>
                                        <Switch
                                            checked={tier.enabled}
                                            onChange={(e) => handleToggle(idx, e.target.checked)}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box sx={{ mt: 3 }}>
                    <Button variant="contained" onClick={handleSave} disabled={loading}>
                        💾 저장
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
}
